import types from '../mutation-types';
import MetaAgentAPI from '../../api/metaAgent';
import { throwErrorMessage, parseAPIErrorResponse } from '../utils/api';
import { useAlert } from 'dashboard/composables';

const freshState = () => ({
  sessions: [],
  currentSessionId: null,
  messages: [],
  auditLogs: [],
  pendingAttachments: [],
  config: { capacidades: null, sugestoes: [], ativo: true },
  uiFlags: {
    isSending: false,
    isFetching: false,
    isUndoing: false,
  },
});

export const state = freshState();

export const getters = {
  getSessions($state) {
    return $state.sessions;
  },
  getCurrentSessionId($state) {
    return $state.currentSessionId;
  },
  getMessages($state) {
    return $state.messages;
  },
  getAuditLogs($state) {
    return $state.auditLogs;
  },
  getUIFlags($state) {
    return $state.uiFlags;
  },
  getPendingAttachments($state) {
    return $state.pendingAttachments;
  },
  getConfig($state) {
    return $state.config;
  },
};

// Como `throwErrorMessage`, mas guardando status e corpo da resposta: a tela do
// dia a dia aplica sobre eles a regra da configuração (texto do servidor, como o
// 403 da conta desligada, ou aviso genérico, como no 429 do limite). Só status e
// corpo — nada de headers/config do pedido. O admin não muda.
const lancarComResposta = error => {
  const wrapped = new Error(parseAPIErrorResponse(error));
  if (error?.response) {
    wrapped.response = {
      status: error.response.status,
      data: error.response.data,
    };
  }
  // `code` (ex.: ECONNABORTED de um timeout do axios) não vem dentro de
  // `response` — sem isso, o chat do dia a dia não consegue distinguir uma
  // consulta que demorou demais de qualquer outro erro sem resposta.
  if (error?.code) wrapped.code = error.code;
  throw wrapped;
};

export const buildActions = api => {
  let inFlightController = null;
  return {
    fetchSessions: async ({ commit }) => {
      commit(types.SET_META_AGENT_UI_FLAG, { isFetching: true });
      try {
        const { data } = await api.getSessions();
        commit(types.SET_META_AGENT_SESSIONS, data);
      } catch (error) {
        // ignore — empty history is a valid state
      } finally {
        commit(types.SET_META_AGENT_UI_FLAG, { isFetching: false });
      }
    },

    fetchMessages: async ({ commit }, sessionId) => {
      commit(types.SET_META_AGENT_UI_FLAG, { isFetching: true });
      try {
        const { data } = await api.getMessages(sessionId);
        commit(types.SET_META_AGENT_CURRENT_SESSION, sessionId);
        commit(types.SET_META_AGENT_MESSAGES, data);
        // Audit timeline is appended per live turn; a session switch starts fresh.
        commit(types.SET_META_AGENT_AUDIT_LOGS, []);
        commit(types.CLEAR_META_AGENT_PENDING_ATTACHMENTS);
      } catch (error) {
        // Code review do PR #142: `throwErrorMessage` descarta o status HTTP —
        // MetaAgentChat precisa distinguir 404 (sessão inexistente/de outro
        // usuário) de qualquer outro erro (rede, 5xx) pra não mostrar "não
        // encontrada" quando na verdade é uma falha transitória.
        const wrapped = new Error(parseAPIErrorResponse(error));
        wrapped.status = error?.response?.status;
        throw wrapped;
      } finally {
        commit(types.SET_META_AGENT_UI_FLAG, { isFetching: false });
      }
    },

    uploadAttachment: async ({ commit }, file) => {
      try {
        const { data } = await api.uploadFile(file);
        commit(types.ADD_META_AGENT_PENDING_ATTACHMENT, data);
        return data;
      } catch (error) {
        throwErrorMessage(error);
        return null;
      }
    },

    removeAttachment: ({ commit }, signedId) => {
      commit(types.REMOVE_META_AGENT_PENDING_ATTACHMENT, signedId);
    },

    // `isRetry`: o "Tentar de novo" do aviso de erro do dia a dia chama de novo
    // com o mesmo conteúdo — sem isso, a bolha do usuário duplicava na tela a
    // cada tentativa (o envio original já a tinha deixado lá, otimista).
    sendMessage: async (
      { commit, state: $state },
      { sessionId, content, isRetry = false }
    ) => {
      commit(types.SET_META_AGENT_UI_FLAG, { isSending: true });
      if (!isRetry) {
        commit(types.APPEND_META_AGENT_MESSAGE, { role: 'user', content });
      }
      inFlightController = new AbortController();
      const attachments = $state.pendingAttachments.map(a => ({
        signed_id: a.signed_id,
        filename: a.filename,
        kind: a.kind,
        preview: a.preview,
      }));
      try {
        const { data } = await api.sendMessage(
          {
            content,
            session_id: sessionId || $state.currentSessionId || undefined,
            attachments: attachments.length ? attachments : undefined,
          },
          { signal: inFlightController.signal }
        );
        if (data.session_id) {
          commit(types.SET_META_AGENT_CURRENT_SESSION, data.session_id);
        }
        if (data.message) {
          // Attach the turn's audit logs to the assistant message so the chat can
          // render diffs inline; the flat auditLogs list feeds the audit panel.
          commit(types.APPEND_META_AGENT_MESSAGE, {
            role: 'assistant',
            ...data.message,
            audit_logs: data.audit_logs || [],
          });
        }
        if (data.audit_logs && data.audit_logs.length) {
          commit(types.APPEND_META_AGENT_AUDIT_LOGS, data.audit_logs);
        }
        commit(types.CLEAR_META_AGENT_PENDING_ATTACHMENTS);
        return data;
      } catch (error) {
        if (error.name !== 'CanceledError' && error.code !== 'ERR_CANCELED') {
          lancarComResposta(error);
        }
      } finally {
        inFlightController = null;
        commit(types.SET_META_AGENT_UI_FLAG, { isSending: false });
      }
      return null;
    },

    renameSession: async ({ commit }, { id, title }) => {
      try {
        const { data } = await api.updateSession(id, { title });
        commit(types.UPDATE_META_AGENT_SESSION, data);
        return data;
      } catch (error) {
        throwErrorMessage(error);
        return null;
      }
    },

    deleteSession: async ({ commit, state: $state }, id) => {
      try {
        await api.deleteSession(id);
        commit(types.REMOVE_META_AGENT_SESSION, id);
        if ($state.currentSessionId === id) {
          commit(types.SET_META_AGENT_CURRENT_SESSION, null);
          commit(types.SET_META_AGENT_MESSAGES, []);
          commit(types.SET_META_AGENT_AUDIT_LOGS, []);
        }
      } catch (error) {
        throwErrorMessage(error);
      }
    },

    regenerateLast: async ({ commit, state: $state }) => {
      if (!$state.currentSessionId) return null;
      commit(types.SET_META_AGENT_UI_FLAG, { isSending: true });
      commit(types.POP_LAST_ASSISTANT_MESSAGE);
      try {
        const { data } = await api.regenerate($state.currentSessionId);
        if (data.message) {
          commit(types.APPEND_META_AGENT_MESSAGE, {
            role: 'assistant',
            ...data.message,
            audit_logs: data.audit_logs || [],
          });
        }
        if (data.audit_logs && data.audit_logs.length) {
          commit(types.APPEND_META_AGENT_AUDIT_LOGS, data.audit_logs);
        }
        return data;
      } catch (error) {
        lancarComResposta(error);
        return null;
      } finally {
        commit(types.SET_META_AGENT_UI_FLAG, { isSending: false });
      }
    },

    stopGeneration: ({ commit }) => {
      if (inFlightController) inFlightController.abort();
      inFlightController = null;
      commit(types.SET_META_AGENT_UI_FLAG, { isSending: false });
    },

    // Ressincroniza só as mensagens da sessão atual (usado pelo cartão de
    // confirmação depois de um 409/410/erro inesperado). De propósito NÃO usa
    // fetchMessages: essa action também zera pendingAttachments (o admin pode
    // ter algo digitado/anexado pra próxima mensagem) e a lista plana de audit
    // logs — efeitos colaterais que não fazem sentido só pra atualizar o
    // status de um cartão.
    resyncMessages: async ({ commit, state: $state }) => {
      if (!$state.currentSessionId) return null;
      try {
        const { data } = await api.getMessages($state.currentSessionId);
        commit(types.SET_META_AGENT_MESSAGES, data);
        return data;
      } catch (error) {
        useAlert(parseAPIErrorResponse(error));
        return null;
      }
    },

    // Confirmação garantida pelo servidor: o cartão pending vira
    // applied/failed. Erros (409 já decidida, 410 expirada, ou falha
    // inesperada) são responsabilidade de quem chama — o cartão decide se
    // ressincroniza ou mostra um aviso inline.
    confirmPendingAction: async ({ commit }, auditId) => {
      const { data } = await api.confirmPendingAction(auditId);
      commit(types.UPDATE_META_AGENT_AUDIT_LOG, data);
      return data;
    },

    cancelPendingAction: async ({ commit }, auditId) => {
      const { data } = await api.cancelPendingAction(auditId);
      commit(types.UPDATE_META_AGENT_AUDIT_LOG, data);
      return data;
    },

    undoAction: async ({ commit }, auditId) => {
      commit(types.SET_META_AGENT_UI_FLAG, { isUndoing: true });
      try {
        const { data } = await api.undo(auditId);
        commit(types.UPDATE_META_AGENT_AUDIT_LOG, data);
        return data;
      } catch (error) {
        throwErrorMessage(error);
      } finally {
        commit(types.SET_META_AGENT_UI_FLAG, { isUndoing: false });
      }
      return null;
    },

    clearSession: ({ commit }) => {
      commit(types.SET_META_AGENT_CURRENT_SESSION, null);
      commit(types.SET_META_AGENT_MESSAGES, []);
      commit(types.SET_META_AGENT_AUDIT_LOGS, []);
      commit(types.CLEAR_META_AGENT_PENDING_ATTACHMENTS);
    },

    fetchConfig: async ({ commit }) => {
      try {
        const { data } = await api.getConfig();
        commit(types.SET_CLICK_AGENT_CONFIG, data);
      } catch (error) {
        // sem configuração lida, a tela usa as sugestões padrão
      }
    },
  };
};

export const actions = buildActions(MetaAgentAPI);

export const mutations = {
  [types.SET_META_AGENT_UI_FLAG]($state, data) {
    $state.uiFlags = { ...$state.uiFlags, ...data };
  },
  [types.SET_META_AGENT_SESSIONS]($state, sessions) {
    $state.sessions = sessions || [];
  },
  [types.SET_META_AGENT_CURRENT_SESSION]($state, sessionId) {
    $state.currentSessionId = sessionId;
  },
  [types.SET_META_AGENT_MESSAGES]($state, messages) {
    $state.messages = messages || [];
  },
  [types.APPEND_META_AGENT_MESSAGE]($state, message) {
    $state.messages = [...$state.messages, message];
  },
  [types.SET_META_AGENT_AUDIT_LOGS]($state, auditLogs) {
    $state.auditLogs = auditLogs || [];
  },
  [types.APPEND_META_AGENT_AUDIT_LOGS]($state, auditLogs) {
    $state.auditLogs = [...$state.auditLogs, ...(auditLogs || [])];
  },
  [types.UPDATE_META_AGENT_AUDIT_LOG]($state, auditLog) {
    $state.auditLogs = $state.auditLogs.map(log =>
      log.id === auditLog.id ? { ...log, ...auditLog } : log
    );
    // Keep the copy nested on the message (used for inline diffs) in sync too.
    $state.messages = $state.messages.map(message => {
      if (!message.audit_logs || !message.audit_logs.length) return message;
      return {
        ...message,
        audit_logs: message.audit_logs.map(log =>
          log.id === auditLog.id ? { ...log, ...auditLog } : log
        ),
      };
    });
  },
  [types.UPDATE_META_AGENT_SESSION]($state, session) {
    $state.sessions = $state.sessions.map(s =>
      s.id === session.id ? { ...s, ...session } : s
    );
  },
  [types.REMOVE_META_AGENT_SESSION]($state, id) {
    $state.sessions = $state.sessions.filter(s => s.id !== id);
  },
  [types.POP_LAST_ASSISTANT_MESSAGE]($state) {
    const idx = $state.messages.map(m => m.role).lastIndexOf('assistant');
    if (idx >= 0) $state.messages = $state.messages.filter((_, i) => i !== idx);
  },
  [types.ADD_META_AGENT_PENDING_ATTACHMENT]($state, attachment) {
    $state.pendingAttachments = [...$state.pendingAttachments, attachment];
  },
  [types.REMOVE_META_AGENT_PENDING_ATTACHMENT]($state, signedId) {
    $state.pendingAttachments = $state.pendingAttachments.filter(
      a => a.signed_id !== signedId
    );
  },
  [types.CLEAR_META_AGENT_PENDING_ATTACHMENTS]($state) {
    $state.pendingAttachments = [];
  },
  [types.SET_CLICK_AGENT_CONFIG]($state, config) {
    $state.config = {
      capacidades: config?.capacidades ?? null,
      sugestoes: Array.isArray(config?.sugestoes) ? config.sugestoes : [],
      ativo: config?.ativo !== false,
    };
  },
};

export const createClickAgentModule = api => ({
  namespaced: true,
  actions: buildActions(api),
  state: freshState,
  getters,
  mutations,
});

export default createClickAgentModule(MetaAgentAPI);
